import Link from "next/link";
import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
const Swiper = dynamic(() => import('swiper/react').then(mod => mod.Swiper), { ssr: false });
import { SwiperSlide } from 'swiper/react';
import { Navigation } from "swiper/modules";

import styles from "./categorySlider.module.scss";
import Button from "@/components/Button";
import { IconRenderer } from "@/components/IconRenderer";
import InlineSVG from "@/components/InlineSVG";
import { urlFor } from "@/sanity/lib/image";

import ArrowRight from "/packages/icons/arrow-right.svg";
import ArrowLeft from "/packages/icons/arrow-left.svg";
import SkeletonElement from "../../skeletons/SkeletonElement";

export default function CategorySlider({
  title = "Categories",
  categories = [],
  shopCategories = [],
  loading = false,
}) {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const swiperConfig = {
    modules: [Navigation],
    className: styles.categorySwiper,
    touchEventsTarget: "wrapper",
    breakpoints: {
      1024: { slidesPerView: 5, slidesPerGroup: 5, spaceBetween: 16 },
      768: { slidesPerView: 4, slidesPerGroup: 4, spaceBetween: 14 },
      550: { slidesPerView: 3, slidesPerGroup: 3, spaceBetween: 10 },
      0: { slidesPerView: 2, slidesPerGroup: 2, spaceBetween: 10 },
    },
    navigation: {
      prevEl: `#category-prev`,
      nextEl: `#category-next`,
      clickable: true,
    },
  };

  const isSkeleton = loading || !hasMounted;

  // 🧠 Normalize category refs
  const resolvedCategories = categories.map((category) => {
    if (category._ref) {
      return shopCategories.find((cat) => cat._id === category._ref) || {};
    }
    return category;
  });

  const items = isSkeleton ? Array.from({ length: 5 }) : resolvedCategories;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title}>
          <h2>{title}</h2>
        </div>
        <Button
          type="link"
          href="/shop"
          className="sliderLink"
          prefetch={false}
          ariaLabel="Shop All"
        >
          <span>shop all</span>
        </Button>
      </div>

      <Swiper {...swiperConfig}>
        <div className={styles.prev} id="category-prev">
          <Button type="button" className="slider-arrow" ariaLabel="Previous Slide">
            <ArrowLeft />
          </Button>
        </div>
        <div className={styles.next} id="category-next">
          <Button type="button" className="slider-arrow" ariaLabel="Next Slide">
            <ArrowRight />
          </Button>
        </div>

        {items.map((item, i) => (
          <SwiperSlide key={item?._id || `skeleton-${i}`} className={styles.slide}>
            {isSkeleton ? (
              <div>
                <SkeletonElement type="category-card-icon" />
                <SkeletonElement type="category-card-title" />
              </div>
            ) : (
              <Link href={`/${item.slug?.current}`}>
                <div className={styles.icon}>
                  {item.mediaType === "icon" && item.icon && (
                    <IconRenderer icon={item.icon} />
                   )}
                  {item.mediaType === "image" && item.customIcon?.asset && (
                    <InlineSVG url={urlFor(item.customIcon).url()} />
                  )}
                </div>
                <div className={styles.slideTitle}>{item.title}</div>
              </Link>
            )}
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

